/**
 * The Firebase-signal live-update transport (NM-250).
 *
 * The long-poll (`poll.ts`) holds one API request open until the state changes —
 * fine in dev, but on Cloud Run a held request bills compute for its whole open
 * lifetime. Instead, on a signal-configured deploy the reader keeps its
 * always-open "waiting" connection to **Firebase** (an `EventSource` over
 * Realtime Database's SSE REST — no Firebase SDK) and only calls the API when the
 * server has written a new marker to this account's `/signals/{channel}` path
 * (which a scheduled check does when it finds news). So the API server is hit
 * when there is likely data, not on a held connection.
 *
 * RTDB's stream sends a `put` on connect carrying the node's current value (the
 * baseline), then a `put`/`patch` on every change. The baseline is skipped — the
 * app already loaded current state before this started — and each later event
 * triggers a refresh. `keep-alive` events are ignored.
 *
 * **It degrades to the long-poll.** If Firebase is unreachable (repeated
 * `EventSource` errors — a blocked origin, RTDB down), the transport tears itself
 * down and calls `onFailure`, which the app wires to start the ordinary
 * long-poll: a reader must keep updating even when the cheap path is broken.
 *
 * Every ambient dependency (the `EventSource` constructor, visibility) is
 * injected so the whole thing is unit-testable without a live RTDB or a browser.
 */

/** The channel the server hands the reader on `/api/state` (NM-250). */
export interface SignalChannel {
  /** RTDB base URL, e.g. `https://<project>-default-rtdb.firebaseio.com`. */
  url: string;
  /** This account's own unguessable path segment under `/signals`. */
  channel: string;
}

/**
 * A minimal `EventSource` surface — the subset this transport uses. Typed with
 * `Event` so a real `EventSource` is structurally assignable to it (the browser
 * wiring returns `new EventSource(url)` directly); the transport ignores the
 * event objects themselves.
 */
export interface EventSourceLike {
  addEventListener(type: string, listener: (ev: Event) => void): void;
  onerror: ((ev: Event) => void) | null;
  readyState: number;
  close(): void;
}

export interface SignalTransportDeps {
  /** Fetch current state (and, via its cascade, the feed). Called on each change. */
  refresh: () => void;
  /** Is the page being displayed right now? */
  isVisible: () => boolean;
  /** Subscribe to visibility changes; returns an unsubscribe. */
  onVisibilityChange: (listener: () => void) => () => void;
  /** Build an `EventSource` for a URL (injected for tests). */
  createEventSource: (url: string) => EventSourceLike;
  /**
   * Called when the signal path has failed too many times to trust — the caller
   * falls back to the long-poll. Fired at most once; the transport stops itself
   * first.
   */
  onFailure: () => void;
}

/**
 * How many consecutive connection errors before giving up on the signal path and
 * falling back to the long-poll. `EventSource` reconnects on its own between
 * these, so this is "the endpoint is persistently broken", not a blip.
 */
export const SIGNAL_MAX_ERRORS = 3;

/**
 * Start waiting on the RTDB signal. Returns a `stop()` that closes the stream and
 * removes the visibility listener (parity with `startPolling`).
 *
 * The stream is closed while the tab is hidden (the wake-up will be caught up on
 * return) and reopened on becoming visible, with an immediate refresh so nothing
 * that changed while away is missed.
 */
export function startSignalTransport(channel: SignalChannel, deps: SignalTransportDeps): () => void {
  const streamUrl = `${channel.url}/signals/${encodeURIComponent(channel.channel)}.json`;
  const state = { stopped: false, errors: 0 };
  let source: EventSourceLike | null = null;
  // The first `put` on a fresh connection is the node's current value, not a
  // change — skip it. Reset every time we (re)open, since each connection replays
  // the baseline.
  let sawBaseline = false;

  const close = (): void => {
    if (source !== null) {
      source.close();
      source = null;
    }
  };

  const fail = (): void => {
    if (state.stopped) return;
    state.stopped = true;
    close();
    deps.onFailure();
  };

  const open = (): void => {
    if (state.stopped || source !== null) return;
    sawBaseline = false;
    const es = deps.createEventSource(streamUrl);
    source = es;
    // `put` carries the whole node (the baseline on connect, then each replacing
    // change); `patch` is a partial change and never the baseline.
    es.addEventListener('put', () => {
      state.errors = 0;
      if (!sawBaseline) {
        sawBaseline = true;
        return;
      }
      deps.refresh();
    });
    es.addEventListener('patch', () => {
      state.errors = 0;
      deps.refresh();
    });
    es.onerror = (): void => {
      state.errors += 1;
      if (state.errors >= SIGNAL_MAX_ERRORS) fail();
    };
  };

  const unsubscribe = deps.onVisibilityChange(() => {
    if (state.stopped) return;
    if (deps.isVisible()) {
      // Back in front of the user: reopen and catch up on anything that landed
      // while the stream was closed.
      open();
      deps.refresh();
    } else {
      // Hidden: drop the connection rather than hold it while nobody reads.
      close();
    }
  });

  if (deps.isVisible()) open();

  return () => {
    state.stopped = true;
    close();
    unsubscribe();
  };
}
